import { Box, Skeleton } from "@mui/material";
import React from "react";
import { FieldContainer } from "../shared/TextField/FormField.styles";
import { MfaContent, MfaRow } from "./MfaSettings.styles";
import {
  DisableSection,
  EditReport,
  FieldRow,
  GeneralInfoText,
  GeneralInfoWrapper,
  ProfileEmailNotificationsButtonWrapper,
  ProfileEmailNotificationsGrid,
  ProfileEmailNotificationsGridLine,
  ProfileEmailNotificationsGridRows,
  ProfileEmailNotificationsHeader,
  ProfileEmailNotificationsOption,
  ProfileEmailNotificationsOptionGrid,
} from "./Profile.styles";

const PROFILE_FIELD_ROWS = [
  ["firstName", "lastName"],
  ["email", "address"],
  ["phone", "mobile"],
];
const ACCOUNT_FIELD_ROWS = [
  ["accountName"],
  ["samples", "phone"],
  ["secondaryPhone"],
  ["type"],
  ["limsId", "officeFax"],
  ["phlebotomy"],
  ["billingAddress"],
];

const ActionSkeleton: React.FC<{ width: number }> = ({ width }) => (
  <Skeleton
    aria-hidden="true"
    variant="rectangular"
    width={width}
    height={36}
    sx={{ borderRadius: "18px" }}
  />
);

const FieldSkeleton = () => (
  <FieldContainer edit={false} $isError={false}>
    <Skeleton aria-hidden="true" variant="text" width={90} height={20} />
    <Skeleton aria-hidden="true" variant="text" width="100%" height={24} />
  </FieldContainer>
);

const FieldSkeletonRows: React.FC<{ rows: string[][] }> = ({ rows }) => (
  <>
    {rows.map((fields) => (
      <FieldRow key={fields[0]}>
        {fields.map((field) => (
          <FieldSkeleton key={field} />
        ))}
      </FieldRow>
    ))}
  </>
);

const NotificationSkeletonRow: React.FC<{
  description?: boolean;
  indent?: boolean;
  sectionHeader?: boolean;
}> = ({ description = false, indent = false, sectionHeader = false }) => (
  <ProfileEmailNotificationsGridRows>
    {indent ? (
      <ProfileEmailNotificationsOption indent>
        <Skeleton aria-hidden="true" variant="text" width={170} height={24} />
      </ProfileEmailNotificationsOption>
    ) : (
      <ProfileEmailNotificationsOptionGrid>
        <ProfileEmailNotificationsOption>
          <Skeleton
            aria-hidden="true"
            variant="text"
            width={sectionHeader ? 210 : 170}
            height={24}
          />
        </ProfileEmailNotificationsOption>
        {description && (
          <ProfileEmailNotificationsHeader>
            <Skeleton
              aria-hidden="true"
              variant="text"
              width="85%"
              height={20}
            />
          </ProfileEmailNotificationsHeader>
        )}
      </ProfileEmailNotificationsOptionGrid>
    )}
    {!sectionHeader && (
      <Box sx={{ width: "100%" }}>
        <Skeleton aria-hidden="true" variant="text" width={95} height={24} />
        <Skeleton
          aria-hidden="true"
          variant="rectangular"
          width="100%"
          height={1}
        />
      </Box>
    )}
    {!sectionHeader && (
      <Skeleton
        aria-hidden="true"
        variant="text"
        width="85%"
        height={20}
        sx={{ "@media (max-width: 768px)": { display: "none" } }}
      />
    )}
  </ProfileEmailNotificationsGridRows>
);

const ProfileLoadingSkeleton: React.FC<{
  showCfcNotifications: boolean;
  showMfaSettings: boolean;
}> = ({ showCfcNotifications, showMfaSettings }) => (
  <Box role="status" aria-label="Loading profile">
    <GeneralInfoWrapper data-testid="profile-details-skeleton">
      <GeneralInfoText>MY PROFILE</GeneralInfoText>
      <FieldSkeletonRows rows={PROFILE_FIELD_ROWS} />
      <EditReport>
        <ActionSkeleton width={86} />
      </EditReport>
    </GeneralInfoWrapper>

    <GeneralInfoWrapper data-testid="notification-settings-skeleton">
      <GeneralInfoText>EMAIL NOTIFICATIONS</GeneralInfoText>
      <ProfileEmailNotificationsGrid>
        <DisableSection>
          <NotificationSkeletonRow description />
          {showCfcNotifications && <NotificationSkeletonRow description />}
          <NotificationSkeletonRow description />
          <ProfileEmailNotificationsGridLine />
          <NotificationSkeletonRow description sectionHeader />
          <NotificationSkeletonRow indent />
          <NotificationSkeletonRow indent />
          <ProfileEmailNotificationsGridLine />
          <NotificationSkeletonRow description sectionHeader />
          <NotificationSkeletonRow indent />
          <NotificationSkeletonRow indent />
        </DisableSection>
        <ProfileEmailNotificationsGridLine />
        <NotificationSkeletonRow />
      </ProfileEmailNotificationsGrid>
      <ProfileEmailNotificationsButtonWrapper>
        <ActionSkeleton width={100} />
        <ActionSkeleton width={100} />
      </ProfileEmailNotificationsButtonWrapper>
    </GeneralInfoWrapper>

    <GeneralInfoWrapper data-testid="account-details-skeleton">
      <GeneralInfoText>MY ACCOUNT</GeneralInfoText>
      <FieldSkeletonRows rows={ACCOUNT_FIELD_ROWS} />
    </GeneralInfoWrapper>

    {showMfaSettings && (
      <GeneralInfoWrapper data-testid="security-settings-skeleton">
        <GeneralInfoText>SECURITY</GeneralInfoText>
        <MfaRow>
          <MfaContent>
            <Skeleton
              aria-hidden="true"
              variant="text"
              width={210}
              height={24}
            />
            <Skeleton
              aria-hidden="true"
              variant="text"
              width="80%"
              height={20}
            />
            <Skeleton
              aria-hidden="true"
              variant="text"
              width={105}
              height={20}
            />
          </MfaContent>
          <Skeleton
            aria-hidden="true"
            variant="rectangular"
            width={42}
            height={24}
            sx={{ borderRadius: "12px" }}
          />
        </MfaRow>
      </GeneralInfoWrapper>
    )}
  </Box>
);

export default ProfileLoadingSkeleton;
